import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../services/api';

interface Student {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatar?: string;
}

interface AuthContextType {
  student: Student | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWith2FA: (tempToken: string, twoFactorCode: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  updateStudent: (student: Student) => void;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [student, setStudent] = useState<Student | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!student;

  useEffect(() => {
    // Verificar si hay token guardado
    const token = localStorage.getItem('student_token');
    console.log('Auth init - token exists:', !!token);
    
    if (token) {
      // Verificar token con el backend
      console.log('Verifying token with backend...');
      authApi.verifyToken()
        .then((response) => {
          console.log('✅ Auth verification successful:', response.data);
          setStudent(response.data);
        })
        .catch((error) => {
          console.log('❌ Auth verification failed:', error);
          localStorage.removeItem('student_token');
        })
        .finally(() => {
          console.log('Auth verification complete, setting loading false');
          setIsLoading(false);
        });
    } else {
      console.log('No token found, setting loading false');
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authApi.login(email, password);
      
      // Si requiere 2FA, lanzar error especial
      if (response.data.requires2FA && response.data.tempToken) {
        const error = new Error('Código de verificación requerido');
        (error as any).requires2FA = true;
        (error as any).tempToken = response.data.tempToken;
        throw error;
      }
      
      const { token, user } = response.data;
      localStorage.setItem('student_token', token);
      setStudent(user);
    } catch (error: any) {
      // Si tiene requires2FA, re-lanzar tal como está
      if (error.requires2FA) {
        throw error;
      }
      // Si no, lanzar error genérico
      throw new Error('Credenciales inválidas');
    }
  };

  const loginWith2FA = async (tempToken: string, twoFactorCode: string) => {
    try {
      const response = await authApi.loginWith2FA({ tempToken, twoFactorCode });
      const { token, user } = response.data;
      
      localStorage.setItem('student_token', token);
      setStudent(user);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Código de autenticación inválido');
    }
  };

  const register = async (data: RegisterData) => {
    try {
      const response = await authApi.register(data);
      const { token, user } = response.data;
      
      localStorage.setItem('student_token', token);
      setStudent(user);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error en el registro');
    }
  };

  const logout = () => {
    localStorage.removeItem('student_token');
    setStudent(null);
  };

  const updateStudent = (updatedStudent: Student) => {
    setStudent(updatedStudent);
  };

  const value: AuthContextType = {
    student,
    isAuthenticated,
    isLoading,
    login,
    loginWith2FA,
    register,
    logout,
    updateStudent,
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};